import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiService } from 'services/ApiService';
import {
  WorkflowStartResponse,
  isWaitingForInput,
  isWorkflowCompleted,
  EnrollmentResponseDto,
} from 'models/api/WorkflowStartResponse';
import { EnrollmentView } from 'components/enrollments/EnrollmentView';
import { FormEnrollment } from 'models/enrollments/FormEnrollment';
import type { Enrollment } from 'models/enrollments/Enrollment';
import type { UserSubmissionDto } from 'models/projects/UserSubmissionDto';
import type { FormSubmission } from 'models/submissions/FormSubmission';
import type { Submission } from 'models/submissions/Submission';
import type { WorkflowTemplate } from 'models/workflows/WorkflowTemplate';
import { utilities } from 'utils/utilities';
import BookingHeader from 'components/booking/BookingHeader';
import ReviewPendingView from 'components/booking/ReviewPendingView';
import WorkflowCompletionView from 'components/booking/WorkflowCompletionView';

type PageState =
  | { status: 'loading' }
  | { status: 'preview'; template: WorkflowTemplate }
  | { status: 'starting' }
  | { status: 'waitingForInput'; response: EnrollmentResponseDto; enrollment: Enrollment }
  | { status: 'reviewPending'; response: EnrollmentResponseDto }
  | { status: 'completed'; workflowInstanceId: string; completionMessage?: string }
  | { status: 'error'; message: string };

function enrollmentFromDto(dto: EnrollmentResponseDto): Enrollment {
  const { enrollmentId, enrollmentType, formId } = dto;
  switch (enrollmentType) {
    case 'FormEnrollment':
    default: {
      const enrollment = new FormEnrollment(enrollmentId, formId ?? utilities.DEFAULT_GUID);
      enrollment.nodeInstanceId = dto.nodeInstanceId ?? utilities.DEFAULT_GUID;
      return enrollment;
    }
  }
}

function applyResponse(
  response: WorkflowStartResponse,
  completionMessage?: string,
): PageState {
  if (isWorkflowCompleted(response)) {
    return {
      status: 'completed',
      workflowInstanceId: response.workflowInstanceId,
      completionMessage,
    };
  }
  if (isWaitingForInput(response)) {
    if (response.enrollmentType === 'FormEnrollment') {
      return {
        status: 'waitingForInput',
        response,
        enrollment: enrollmentFromDto(response),
      };
    }
    return { status: 'reviewPending', response };
  }
  return { status: 'error', message: 'Unexpected response from server.' };
}

const BookingPage: React.FC = () => {
  const { artistId, workflowTemplateId } = useParams<{
    artistId: string;
    workflowTemplateId: string;
  }>();

  const [pageState, setPageState] = useState<PageState>({ status: 'loading' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const templateRef = useRef<WorkflowTemplate | null>(null);

  useEffect(() => {
    if (!artistId || !workflowTemplateId) {
      setPageState({ status: 'error', message: 'Missing artist or workflow identifier.' });
      return;
    }

    ApiService.getArtistWorkflowTemplate(artistId, workflowTemplateId)
      .then((template) => {
        templateRef.current = template;
        setPageState({ status: 'preview', template });
      })
      .catch((err) => {
        console.error('getArtistWorkflowTemplate failed:', err);
        setPageState({
          status: 'error',
          message:
            err?.response?.data?.errors?.[0] ??
            'Failed to load the booking page. Please try again.',
        });
      });
  }, [artistId, workflowTemplateId]);

  const handleBegin = useCallback(async () => {
    if (!workflowTemplateId || !templateRef.current) return;
    setPageState({ status: 'starting' });
    try {
      const response = await ApiService.startWorkflow(workflowTemplateId, templateRef.current.teamId);
      setPageState(applyResponse(response, templateRef.current.completionMessage));
    } catch (err: any) {
      console.error('startWorkflow failed:', err);
      setPageState({
        status: 'error',
        message:
          err?.response?.data?.errors?.[0] ??
          'Failed to start the booking flow. Please try again.',
      });
    }
  }, [workflowTemplateId]);

  const submitToEnrollment = useCallback(
    async (enrollmentId: string, submission: Submission) => {
      setIsSubmitting(true);
      try {
        const response = await ApiService.submitToEnrollment(enrollmentId, submission);
        setPageState(applyResponse(response, templateRef.current?.completionMessage));
      } catch (err: any) {
        console.error('submitToEnrollment failed:', err);
        setPageState({
          status: 'error',
          message:
            err?.response?.data?.errors?.[0] ?? 'Submission failed. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [],
  );

  const handleFormSubmit = useCallback(
    (submissionDto: UserSubmissionDto) => {
      if (pageState.status !== 'waitingForInput') return;

      const formSubmission: FormSubmission = {
        id: utilities.DEFAULT_GUID,
        enrollmentId: pageState.response.enrollmentId,
        nodeInstanceId: pageState.response.nodeInstanceId ?? utilities.DEFAULT_GUID,
        workflowInstanceId: pageState.response.workflowInstanceId,
        submittedByUserId: '',
        type: 'FormSubmission',
        submittedAt: new Date().toISOString(),
        responseJson: JSON.stringify(submissionDto),
      };

      submitToEnrollment(pageState.response.enrollmentId, formSubmission);
    },
    [pageState, submitToEnrollment],
  );

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (pageState.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"
            role="status"
            aria-label="Loading"
          />
          <p className="text-gray-500 text-sm">Loading…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (pageState.status === 'error') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-lg p-8 shadow-sm">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Something went wrong</h2>
          <p className="text-gray-600 text-sm">{pageState.message}</p>
        </div>
      </div>
    );
  }

  // ── Preview ──────────────────────────────────────────────────────────────────
  if (pageState.status === 'preview') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <BookingHeader
          workflowName={pageState.template.name}
          description={pageState.template.description}
        />
        <button
          onClick={handleBegin}
          className="mt-2 px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Begin
        </button>
      </div>
    );
  }

  // ── Starting ─────────────────────────────────────────────────────────────────
  if (pageState.status === 'starting') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"
            role="status"
            aria-label="Starting"
          />
          <p className="text-gray-500 text-sm">Starting…</p>
        </div>
      </div>
    );
  }

  // ── Review pending ───────────────────────────────────────────────────────────
  if (pageState.status === 'reviewPending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <ReviewPendingView />
      </div>
    );
  }

  // ── Completed ────────────────────────────────────────────────────────────────
  if (pageState.status === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <WorkflowCompletionView message={pageState.completionMessage} />
      </div>
    );
  }

  // ── Waiting for input ────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="max-w-2xl w-full">
        <EnrollmentView
          enrollment={pageState.enrollment}
          teamId={pageState.response.teamId ?? templateRef.current?.teamId}
          workflowId={pageState.response.workflowInstanceId}
          nodeId={pageState.response.nodeInstanceId}
          isDisabled={isSubmitting}
          onSubmit={handleFormSubmit}
        />
        {isSubmitting && (
          <div className="flex justify-center mt-4">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPage;
